import { leaveShare } from 'src/apis/share';
import { ShareDto, ShareResourceTypeApi } from 'src/types/share';
import { UserRole } from 'src/types/user';
import { getFolderLeafName, isUnderPath } from 'src/utils/campaignFolderPath';

export type ShareResourceType =
  | 'dashboard'
  | 'dashboardFolder'
  | 'campaign'
  | 'campaignFolder';

const RESOURCE_TYPE_API: Record<ShareResourceType, ShareResourceTypeApi> = {
  dashboard: 'DASHBOARD',
  dashboardFolder: 'DASHBOARD_FOLDER',
  campaign: 'CAMPAIGN',
  campaignFolder: 'CAMPAIGN_FOLDER',
};

export const toShareResourceTypeApi = (
  type: ShareResourceType
): ShareResourceTypeApi => RESOURCE_TYPE_API[type];

/** POST /share requires admin or super. */
export const canShareResources = (role?: UserRole | string | null): boolean =>
  role === UserRole.ADMIN || role === UserRole.SUPER;

export const matchReceivedShare = (
  shares: ShareDto[],
  resourceType: ShareResourceTypeApi,
  resourceId: string
): ShareDto | undefined =>
  shares.find(
    (share) =>
      share.resourceType === resourceType &&
      String(share.resourceId) === String(resourceId)
  );

/**
 * Folder shares may use an internal UUID as resourceId and the folder name/path
 * as resourceName. Match either so sidebar keys still resolve.
 */
export const matchReceivedFolderShare = (
  shares: ShareDto[],
  resourceType: 'DASHBOARD_FOLDER' | 'CAMPAIGN_FOLDER',
  folderKey: string
): ShareDto | undefined =>
  shares.find((share) => {
    if (share.resourceType !== resourceType) return false;
    if (String(share.resourceId) === String(folderKey)) return true;
    if (share.resourceName && share.resourceName === folderKey) return true;
    // Campaign nested path vs leaf name from resourceName
    if (
      resourceType === 'CAMPAIGN_FOLDER' &&
      share.resourceName &&
      getFolderLeafName(folderKey) === share.resourceName
    ) {
      return true;
    }
    return false;
  });

export const getReceivedResourceIds = (
  shares: ShareDto[],
  resourceType: ShareResourceTypeApi
): Set<string> =>
  new Set(
    shares
      .filter((share) => share.resourceType === resourceType)
      .map((share) => String(share.resourceId))
  );

/** Sidebar keys (folder name or path) for received folder shares. */
export const getReceivedFolderKeys = (
  shares: ShareDto[],
  resourceType: 'DASHBOARD_FOLDER' | 'CAMPAIGN_FOLDER'
): Set<string> => {
  const keys = new Set<string>();
  shares
    .filter((share) => share.resourceType === resourceType)
    .forEach((share) => {
      keys.add(String(share.resourceId));
      if (share.resourceName) {
        keys.add(share.resourceName);
      }
    });
  return keys;
};

export const isDashboardInReceivedFolderShare = (
  folderName: string | null | undefined,
  sharedFolderKeys: Set<string>
): boolean => Boolean(folderName && sharedFolderKeys.has(folderName));

export const isCampaignInReceivedFolderShare = (
  folderPath: string | null | undefined,
  sharedFolderKeys: Set<string>
): boolean => {
  if (!folderPath) return false;
  if (sharedFolderKeys.has(folderPath)) return true;
  return Array.from(sharedFolderKeys).some(
    (key) =>
      isUnderPath(folderPath, key) || getFolderLeafName(folderPath) === key
  );
};

export const leaveReceivedShareById = async (
  shareId: string
): Promise<void> => {
  await leaveShare(shareId);
};

/** Item-level received shares whose resourceId is in the given set. */
export const getReceivedItemSharesForIds = (
  shares: ShareDto[],
  itemType: 'DASHBOARD' | 'CAMPAIGN',
  itemIds: Iterable<string>
): ShareDto[] => {
  const idSet = new Set(Array.from(itemIds, String));
  return shares.filter(
    (share) =>
      share.resourceType === itemType && idSet.has(String(share.resourceId))
  );
};

/**
 * Leave a folder share and any item-level shares for resources in that folder
 * (e.g. items shared before the folder was shared).
 */
export const leaveFolderShareWithItemShares = async (
  folderShare: ShareDto,
  itemShares: ShareDto[]
): Promise<void> => {
  await leaveShare(folderShare.id);
  await Promise.all(
    itemShares
      .filter((share) => share.id !== folderShare.id)
      .map((share) => leaveShare(share.id))
  );
};
